import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  PageHeader,
  Radio,
  Result,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  TimePicker,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  ClearOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  GlobalOutlined,
  LeftOutlined,
  PlusCircleOutlined,
  PlusOutlined,
  RightOutlined,
  ScheduleOutlined,
  SettingOutlined,
  SnippetsOutlined,
  SwapOutlined,
  UndoOutlined,
  UserAddOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import moment from 'moment-timezone';
import Auth from 'auth';
import {
  accessControls,
  handleApiError,
  handleApiSuccess,
  MyBreadcrumb,
} from 'utils';
import { timeTrackerAssignableUsersListService } from 'services/time_tracker';
import {
  schedulingActivitiesListService,
  schedulingScheduleGetService,
  schedulingSchedulePublishService,
  schedulingShiftClaimService,
  schedulingShiftClearWeekService,
  schedulingShiftCreateService,
  schedulingShiftDeleteService,
  schedulingShiftDuplicateService,
  schedulingShiftsListService,
  schedulingShiftUpdateService,
  schedulingShiftSwapRequestService,
  schedulingSwapsListService,
  schedulingSwapClaimService,
  schedulingSwapCancelService,
  schedulingAvailabilityListService,
  schedulingAvailabilityCreateService,
  schedulingAvailabilityDeleteService,
  schedulingAvailabilityMatchService,
} from 'services/scheduling';
import {
  formatWeekHeading,
  getEffectiveTimezone,
  getTimezoneSelectOptions,
  getTrackerTimezoneSummary,
  getWeekStartMoment,
  loadStoredTimezone,
  saveStoredTimezone,
} from 'pages/time_tracker/tracker/tracker_timezone';
import './scheduling.css';

const { Text } = Typography;
const { Option } = Select;

function userName(u) {
  if (!u) return 'User';
  return (
    u.display_name ||
    [u.first_name, u.last_name].filter(Boolean).join(' ') ||
    u.email ||
    'User'
  );
}

function userLabel(u) {
  const name = userName(u);
  const email = u?.email ? String(u.email) : '';
  return email ? `${name} (${email})` : name;
}

function userIdOf(u) {
  return String(u?._id || u || '');
}

function formatHours(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

const GRID_START_HOUR = 6;
const GRID_END_HOUR = 22;
const PX_PER_HOUR = 32;
const SNAP_MINUTES = 15;
const SEAT_GUTTER = 36;

function snapMinutes(mins) {
  return Math.round(mins / SNAP_MINUTES) * SNAP_MINUTES;
}

function layoutForShift(startsAt, endsAt, tz, gridStartHour) {
  const start = moment.tz(startsAt, tz);
  const end = moment.tz(endsAt, tz);
  const dayStart = start
    .clone()
    .hour(gridStartHour)
    .minute(0)
    .second(0)
    .millisecond(0);
  const topMin = start.diff(dayStart, 'minutes');
  const durMin = Math.max(SNAP_MINUTES, end.diff(start, 'minutes'));
  return {
    top: Math.max(0, (topMin / 60) * PX_PER_HOUR),
    height: Math.max(28, (durMin / 60) * PX_PER_HOUR),
  };
}

function shiftHourSpan(startsAt, endsAt, tz) {
  const start = moment.tz(startsAt, tz);
  const end = moment.tz(endsAt, tz);
  let endHour = end.hour();
  if (end.minute() > 0 || end.second() > 0 || end.millisecond() > 0) {
    endHour += 1;
  }
  if (!end.isSame(start, 'day')) endHour = 24;
  return {
    startHour: start.hour(),
    endHour: Math.min(24, Math.max(endHour, start.hour() + 1)),
  };
}

function gridBoundsForShifts(allShifts, tz) {
  let start = GRID_START_HOUR;
  let end = GRID_END_HOUR;
  (allShifts || []).forEach((shift) => {
    const span = shiftHourSpan(shift.starts_at, shift.ends_at, tz);
    start = Math.min(start, span.startHour);
    end = Math.max(end, span.endHour);
  });
  start = Math.max(0, start);
  end = Math.min(24, Math.max(end, start + 1));
  return {
    start,
    end,
    height: (end - start) * PX_PER_HOUR,
  };
}

function layoutDaySeats(dayShifts, preview) {
  const seats = [];
  (dayShifts || []).forEach((shift) => {
    const dragged = preview && String(preview.id) === String(shift._id);
    const starts = dragged ? preview.starts_at : shift.starts_at;
    const ends = dragged ? preview.ends_at : shift.ends_at;
    const startMs = new Date(starts).getTime();
    const endMs = new Date(ends).getTime();
    const capacity = Math.max(1, Number(shift.capacity || 1));
    const assignees = shift.assignees || [];
    for (let seatIndex = 0; seatIndex < capacity; seatIndex += 1) {
      seats.push({
        shift,
        seatIndex,
        assignee: assignees[seatIndex] || null,
        starts,
        ends,
        startMs,
        endMs,
      });
    }
  });
  seats.sort(
    (a, b) =>
      a.startMs - b.startMs ||
      a.endMs - b.endMs ||
      String(a.shift._id).localeCompare(String(b.shift._id)) ||
      a.seatIndex - b.seatIndex,
  );
  const laneEnds = [];
  seats.forEach((seat) => {
    let lane = laneEnds.findIndex((end) => seat.startMs >= end);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(seat.endMs);
    } else {
      laneEnds[lane] = seat.endMs;
    }
    seat.lane = lane;
  });
  seats.forEach((seat) => {
    let maxLane = seat.lane;
    seats.forEach((other) => {
      const overlaps = seat.startMs < other.endMs && other.startMs < seat.endMs;
      if (overlaps) maxLane = Math.max(maxLane, other.lane);
    });
    seat.laneCount = maxLane + 1;
  });
  return { seats, laneCount: Math.max(1, laneEnds.length) };
}

function assigneeOptions(schedule, users) {
  const groups = schedule?.member_groups || [];
  if (!groups.length) return users;
  const byId = new Map();
  groups.forEach((group) => {
    (group.members || []).forEach((member) => {
      if (!member) return;
      const id = String(member._id || member);
      if (byId.has(id)) return;
      if (member._id) {
        byId.set(id, member);
        return;
      }
      const found = (users || []).find((user) => String(user._id) === id);
      if (found) byId.set(id, found);
    });
  });
  return [...byId.values()];
}

function snapshotShift(shift) {
  return {
    activity: shift.activity?._id || shift.activity,
    starts_at: new Date(shift.starts_at).toISOString(),
    ends_at: new Date(shift.ends_at).toISOString(),
    capacity: shift.capacity || 1,
    assignees: (shift.assignees || []).map((assignee) => ({
      user: userIdOf(assignee.user),
    })),
    notes: shift.notes || '',
    color: shift.color || '',
    published: shift.published !== false,
  };
}

function buildShiftDraft(shift, day, tz) {
  const start = shift
    ? moment.tz(shift.starts_at, tz)
    : (day || moment.tz(tz))
        .clone()
        .hour(9)
        .minute(0)
        .second(0)
        .millisecond(0);
  const end = shift
    ? moment.tz(shift.ends_at, tz)
    : start.clone().add(8, 'hours');
  return {
    activity: shift?.activity?._id || shift?.activity,
    date: start.clone().startOf('day'),
    start_time: start.clone(),
    end_time: end.clone(),
    capacity: shift?.capacity || 1,
    assignees: (shift?.assignees || []).map((assignee) => userIdOf(assignee.user)),
    notes: shift?.notes || '',
    color: shift?.color || '#1890ff',
    use_color: Boolean(shift?.color),
  };
}

export default function SchedulingBoard() {
  const { id } = useParams();
  const history = useHistory();
  const [permissions, setPermissions] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [canManage, setCanManage] = useState(false);
  const [activities, setActivities] = useState([]);
  const [users, setUsers] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [storedTz, setStoredTz] = useState(loadStoredTimezone);
  const [weekStart, setWeekStart] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [clipboardShift, setClipboardShift] = useState(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishNotify, setPublishNotify] = useState('affected');
  const [publishing, setPublishing] = useState(false);
  const [modalKey, setModalKey] = useState(0);
  const [swaps, setSwaps] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [availOpen, setAvailOpen] = useState(false);
  const [availSaving, setAvailSaving] = useState(false);
  const [availForm] = Form.useForm();
  const [matches, setMatches] = useState([]);
  const [matchNotice, setMatchNotice] = useState(null);
  const [matching, setMatching] = useState(false);
  const [shiftDraft, setShiftDraft] = useState(null);
  const [dragPreview, setDragPreview] = useState(null);
  const [slotList, setSlotList] = useState(null);
  const [raisedShiftId, setRaisedShiftId] = useState(null);
  const [undoStack, setUndoStack] = useState([]);
  const [undoing, setUndoing] = useState(false);
  const undoStackRef = useRef([]);
  const undoingRef = useRef(false);
  const loadSeq = useRef(0);
  const [tzSearch, setTzSearch] = useState('');

  const tz = getEffectiveTimezone(storedTz || schedule?.timezone || '');
  const payload = Auth.getPayload() || {};
  const currentUserId = String(
    payload.userId || payload._id || payload.id || '',
  );
  const hasUpdatePerm = accessControls.can(
    permissions,
    'Scheduling.update:any',
  );
  const canUpdate = hasUpdatePerm && canManage;

  useEffect(() => {
    accessControls
      .fetch()
      .then(setPermissions)
      .catch(() => setPermissions({}));
  }, []);

  useEffect(() => {
    if (!permissions || !accessControls.can(permissions, 'Scheduling.read:any')) {
      return;
    }
    setLoading(true);
    Promise.all([
      schedulingScheduleGetService(id),
      schedulingActivitiesListService(),
      timeTrackerAssignableUsersListService().catch(() => ({
        data: { items: [] },
      })),
    ])
      .then(([sr, ar, ur]) => {
        const value = sr.data?.items?.[0] || null;
        setSchedule(value);
        setCanManage(value?.can_manage !== false);
        setActivities(ar.data?.items || []);
        setUsers(ur.data?.items || []);
        setWeekStart(
          getWeekStartMoment(
            moment(),
            getEffectiveTimezone(storedTz || value?.timezone || ''),
          ),
        );
      })
      .catch(handleApiError)
      .finally(() => setLoading(false));
  }, [id, permissions, storedTz]);

  const loadShifts = useCallback(() => {
    if (!weekStart) return Promise.resolve();
    const seq = ++loadSeq.current;
    setLoading(true);
    const from = weekStart.toISOString();
    const to = weekStart.clone().add(7, 'days').toISOString();
    return Promise.all([
      schedulingShiftsListService({
        scheduleId: id,
        from,
        to,
      }),
      schedulingSwapsListService({ scheduleId: id }).catch(() => ({
        data: { items: [] },
      })),
      schedulingAvailabilityListService({
        scheduleId: id,
        from,
        to,
      }).catch(() => ({ data: { items: [] } })),
    ])
      .then(([r, sw, av]) => {
        if (seq !== loadSeq.current) return;
        setShifts(r.data?.items || []);
        if (r.data?.meta?.can_manage != null) {
          setCanManage(!!r.data.meta.can_manage);
        }
        setSwaps(sw.data?.items || []);
        setAvailability(av.data?.items || []);
      })
      .catch((err) => {
        if (seq === loadSeq.current) handleApiError(err);
      })
      .finally(() => {
        if (seq === loadSeq.current) setLoading(false);
      });
  }, [id, weekStart]);

  useEffect(() => {
    loadShifts();
  }, [loadShifts]);

  useEffect(() => {
    if (!modalOpen || !shiftDraft) return;
    form.setFieldsValue(shiftDraft);
  }, [modalOpen, modalKey, shiftDraft, form]);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => weekStart?.clone().add(i, 'days')),
    [weekStart],
  );

  const heading = weekStart
    ? formatWeekHeading(weekStart, getWeekStartMoment(moment(), tz))
    : null;

  const todayKey = moment.tz(tz).format('YYYY-MM-DD');
  const grid = useMemo(() => gridBoundsForShifts(shifts, tz), [shifts, tz]);
  const tzOptions = useMemo(
    () => getTimezoneSelectOptions(tzSearch, storedTz || schedule?.timezone),
    [tzSearch, storedTz, schedule],
  );
  const pickerUsers = useMemo(
    () => assigneeOptions(schedule, users),
    [schedule, users],
  );
  const assigneeChoices = useMemo(() => {
    const byId = new Map(pickerUsers.map((user) => [String(user._id), user]));
    (editing?.assignees || []).forEach((assignee) => {
      const user = assignee.user;
      if (user && user._id) byId.set(String(user._id), user);
    });
    matches.forEach((match) => {
      const user = match.user;
      if (user && user._id) byId.set(String(user._id), user);
    });
    return [...byId.values()];
  }, [pickerUsers, editing, matches]);
  const memberGroupsSet = (schedule?.member_groups || []).length > 0;

  const draftCount = useMemo(
    () => shifts.filter((s) => s.published === false).length,
    [shifts],
  );

  const hoursByUser = useMemo(() => {
    const map = new Map();
    shifts.forEach((shift) => {
      const secs = Math.max(
        0,
        (new Date(shift.ends_at) - new Date(shift.starts_at)) / 1000,
      );
      (shift.assignees || []).forEach((a) => {
        const uid = userIdOf(a.user);
        const prev = map.get(uid) || {
          id: uid,
          name: userName(a.user),
          seconds: 0,
        };
        prev.seconds += secs;
        map.set(uid, prev);
      });
    });
    return [...map.values()].sort((a, b) => b.seconds - a.seconds);
  }, [shifts]);

  const pushUndo = (entry) => {
    const next = [...undoStackRef.current, entry].slice(-30);
    undoStackRef.current = next;
    setUndoStack(next);
  };

  const undoLast = () => {
    if (undoingRef.current) return;
    const entry = undoStackRef.current[undoStackRef.current.length - 1];
    if (!entry) return;
    const next = undoStackRef.current.slice(0, -1);
    undoStackRef.current = next;
    setUndoStack(next);
    undoingRef.current = true;
    setUndoing(true);
    entry
      .run()
      .then((r) => {
        if (r) handleApiSuccess(r);
        loadShifts();
      })
      .catch((err) => {
        handleApiError(err);
        const restored = [...undoStackRef.current, entry].slice(-30);
        undoStackRef.current = restored;
        setUndoStack(restored);
      })
      .finally(() => {
        undoingRef.current = false;
        setUndoing(false);
      });
  };

  const openShift = (shift, day) => {
    setEditing(shift || null);
    setShiftDraft(buildShiftDraft(shift, day, tz));
    setModalKey((k) => k + 1);
    setMatches([]);
    setMatchNotice(null);
    setModalOpen(true);
  };

  const copyShift = (shift) => {
    setClipboardShift(shift);
    message.success(
      'Shift copied — click + on a day to paste, or use Paste on Add shift',
    );
  };

  const pasteOntoDay = (day) => {
    if (!clipboardShift || !day) return;
    const srcStart = moment.tz(clipboardShift.starts_at, tz);
    const srcEnd = moment.tz(clipboardShift.ends_at, tz);
    const durationMin = srcEnd.diff(srcStart, 'minutes');
    const start = day
      .clone()
      .hour(srcStart.hour())
      .minute(srcStart.minute())
      .second(0);
    const end = start.clone().add(durationMin, 'minutes');
    schedulingShiftDuplicateService(clipboardShift._id, {
      starts_at: start.toISOString(),
      ends_at: end.toISOString(),
    })
      .then((r) => {
        const created = r.data?.items?.[0];
        if (created?._id) {
          pushUndo({
            label: 'Paste shift',
            run: () => schedulingShiftDeleteService(created._id),
          });
        }
        handleApiSuccess(r);
        loadShifts();
      })
      .catch(handleApiError);
  };

  const submitShift = () => {
    form.validateFields().then((values) => {
      if (editing && !values.activity) {
        message.error(
          'Shift details did not load. Close the editor and open it again.',
        );
        return;
      }
      const submittedAssignees = values.assignees || [];
      const draftAssignees = shiftDraft?.assignees || [];
      if (
        editing &&
        (editing.assignees || []).length > 0 &&
        submittedAssignees.length === 0 &&
        draftAssignees.length === 0
      ) {
        message.error(
          'Assignees did not load. Close the editor and open the shift again before saving.',
        );
        return;
      }

      const date = values.date.format('YYYY-MM-DD');
      const startHm = values.start_time.format('HH:mm');
      const endHm = values.end_time.format('HH:mm');
      const start = moment.tz(`${date} ${startHm}`, 'YYYY-MM-DD HH:mm', tz);
      let end = moment.tz(`${date} ${endHm}`, 'YYYY-MM-DD HH:mm', tz);
      if (!end.isAfter(start)) end = end.add(1, 'day');

      const payloadBody = {
        schedule: id,
        activity: values.activity,
        starts_at: start.toISOString(),
        ends_at: end.toISOString(),
        capacity: values.capacity,
        assignees: submittedAssignees.map((user) => ({ user })),
        notes: values.notes || '',
        color: values.use_color ? values.color || '' : '',
      };
      const previous = editing ? snapshotShift(editing) : null;

      setSaving(true);
      const req = editing
        ? schedulingShiftUpdateService(editing._id, payloadBody)
        : schedulingShiftCreateService(payloadBody);
      req
        .then((r) => {
          const saved = r.data?.items?.[0];
          if (editing && previous) {
            pushUndo({
              label: 'Edit shift',
              run: () => schedulingShiftUpdateService(editing._id, previous),
            });
          } else if (saved?._id) {
            pushUndo({
              label: 'Create shift',
              run: () => schedulingShiftDeleteService(saved._id),
            });
          }
          handleApiSuccess(r);
          setModalOpen(false);
          loadShifts();
        })
        .catch(handleApiError)
        .finally(() => setSaving(false));
    });
  };

  const runAction = (request) => {
    request
      .then((r) => {
        handleApiSuccess(r);
        loadShifts();
      })
      .catch(handleApiError);
  };

  const persistShiftTimes = (shift, startsAt, endsAt) => {
    const previous = {
      starts_at: new Date(shift.starts_at).toISOString(),
      ends_at: new Date(shift.ends_at).toISOString(),
    };
    schedulingShiftUpdateService(shift._id, {
      starts_at: startsAt.toISOString(),
      ends_at: endsAt.toISOString(),
    })
      .then(() => {
        pushUndo({
          label: 'Move shift',
          run: () => schedulingShiftUpdateService(shift._id, previous),
        });
        loadShifts();
      })
      .catch(handleApiError);
  };

  const deleteShift = (shift) => {
    const previous = {
      schedule: id,
      ...snapshotShift(shift),
    };
    const activity = shift.activity?.name || 'this shift';
    const when = `${moment.tz(shift.starts_at, tz).format('ddd MMM D, h:mm A')} – ${moment
      .tz(shift.ends_at, tz)
      .format('h:mm A')}`;
    Modal.confirm({
      title: 'Delete this shift?',
      content: `${activity} · ${when}`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () =>
        schedulingShiftDeleteService(shift._id)
          .then((r) => {
            pushUndo({
              label: 'Delete shift',
              run: () => schedulingShiftCreateService(previous),
            });
            handleApiSuccess(r);
            return loadShifts();
          })
          .catch(handleApiError),
    });
  };

  const clearThisWeek = () => {
    if (!weekStart) return;
    const count = shifts.length;
    const rangeLabel = heading?.range || 'this week';
    if (!count) {
      message.info('This week has no shifts to clear.');
      return;
    }
    Modal.confirm({
      title: 'Clear this week?',
      content: `This removes ${count} shift${
        count === 1 ? '' : 's'
      } from ${rangeLabel}. Undo puts them back.`,
      okText: 'Clear week',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () =>
        schedulingShiftClearWeekService({
          scheduleId: id,
          from: weekStart.toISOString(),
          to: weekStart.clone().add(7, 'days').toISOString(),
        })
          .then((r) => {
            const snapshots = r.data?.items || [];
            if (snapshots.length) {
              pushUndo({
                label: 'Clear week',
                run: () =>
                  Promise.all(
                    snapshots.map((body) => schedulingShiftCreateService(body)),
                  ),
              });
            }
            handleApiSuccess(r);
            return loadShifts();
          })
          .catch(handleApiError),
    });
  };

  const duplicateShift = (shiftId) => {
    schedulingShiftDuplicateService(shiftId)
      .then((r) => {
        const created = r.data?.items?.[0];
        if (created?._id) {
          pushUndo({
            label: 'Duplicate shift',
            run: () => schedulingShiftDeleteService(created._id),
          });
        }
        handleApiSuccess(r);
        loadShifts();
      })
      .catch(handleApiError);
  };

  const ignoreClickRef = useRef(false);

  const openSlotList = (dayLabel, items) => {
    setSlotList({
      dayLabel,
      ids: (items || []).map((item) => String(item.shift?._id || item._id)),
    });
  };

  const startMoveShift = (event, shift) => {
    if (!canUpdate || event.button !== 0) return;
    if (event.target.closest('.scheduling-shift-actions, .scheduling-shift-resize, button, a')) {
      return;
    }
    const originY = event.clientY;
    const originX = event.clientX;
    let dragged = false;
    const start = moment.tz(shift.starts_at, tz);
    const end = moment.tz(shift.ends_at, tz);
    const duration = end.diff(start, 'minutes');
    const onMove = (ev) => {
      if (
        Math.abs(ev.clientY - originY) > 4 ||
        Math.abs(ev.clientX - originX) > 4
      ) {
        if (!dragged) event.preventDefault();
        dragged = true;
        ignoreClickRef.current = true;
      }
      const delta = snapMinutes(((ev.clientY - originY) / PX_PER_HOUR) * 60);
      const el = document.elementFromPoint(ev.clientX, ev.clientY);
      const dayEl = el && el.closest('[data-day]');
      const dayKey = dayEl ? dayEl.getAttribute('data-day') : start.format('YYYY-MM-DD');
      const nextStart = moment.tz(
        `${dayKey} ${start.clone().add(delta, 'minutes').format('HH:mm')}`,
        'YYYY-MM-DD HH:mm',
        tz,
      );
      setDragPreview({
        id: shift._id,
        starts_at: nextStart.toISOString(),
        ends_at: nextStart.clone().add(duration, 'minutes').toISOString(),
      });
    };
    const onUp = (ev) => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      const delta = snapMinutes(((ev.clientY - originY) / PX_PER_HOUR) * 60);
      const el = document.elementFromPoint(ev.clientX, ev.clientY);
      const dayEl = el && el.closest('[data-day]');
      const dayKey = dayEl ? dayEl.getAttribute('data-day') : start.format('YYYY-MM-DD');
      const nextStart = moment.tz(
        `${dayKey} ${start.clone().add(delta, 'minutes').format('HH:mm')}`,
        'YYYY-MM-DD HH:mm',
        tz,
      );
      const nextEnd = nextStart.clone().add(duration, 'minutes');
      setDragPreview(null);
      if (
        dragged &&
        (nextStart.toISOString() !== start.toISOString() ||
          dayKey !== start.format('YYYY-MM-DD'))
      ) {
        persistShiftTimes(shift, nextStart, nextEnd);
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const startResizeShift = (event, shift) => {
    if (!canUpdate || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    ignoreClickRef.current = true;
    const originY = event.clientY;
    const start = moment.tz(shift.starts_at, tz);
    const end = moment.tz(shift.ends_at, tz);
    const onMove = (ev) => {
      const delta = snapMinutes(((ev.clientY - originY) / PX_PER_HOUR) * 60);
      const nextEnd = end.clone().add(delta, 'minutes');
      if (!nextEnd.isAfter(start.clone().add(SNAP_MINUTES, 'minutes'))) return;
      setDragPreview({
        id: shift._id,
        starts_at: start.toISOString(),
        ends_at: nextEnd.toISOString(),
      });
    };
    const onUp = (ev) => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      const delta = snapMinutes(((ev.clientY - originY) / PX_PER_HOUR) * 60);
      const nextEnd = end.clone().add(delta, 'minutes');
      setDragPreview(null);
      if (
        delta !== 0 &&
        nextEnd.isAfter(start.clone().add(SNAP_MINUTES, 'minutes'))
      ) {
        persistShiftTimes(shift, start, nextEnd);
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const submitAvailability = () => {
    availForm.validateFields().then((values) => {
      const date = values.date.format('YYYY-MM-DD');
      const startHm = values.start_time.format('HH:mm');
      const endHm = values.end_time.format('HH:mm');
      const start = moment.tz(`${date} ${startHm}`, 'YYYY-MM-DD HH:mm', tz);
      let end = moment.tz(`${date} ${endHm}`, 'YYYY-MM-DD HH:mm', tz);
      if (!end.isAfter(start)) end = end.add(1, 'day');
      setAvailSaving(true);
      schedulingAvailabilityCreateService({
        schedule: id,
        type: values.type,
        starts_at: start.toISOString(),
        ends_at: end.toISOString(),
        note: values.note || '',
      })
        .then((r) => {
          handleApiSuccess(r);
          availForm.resetFields();
          loadShifts();
        })
        .catch(handleApiError)
        .finally(() => setAvailSaving(false));
    });
  };

  const lookupMatches = () => {
    if (!canUpdate) return;
    form
      .validateFields(['date', 'start_time', 'end_time'])
      .then((values) => {
        const date = values.date.format('YYYY-MM-DD');
        const startHm = values.start_time.format('HH:mm');
        const endHm = values.end_time.format('HH:mm');
        const start = moment.tz(`${date} ${startHm}`, 'YYYY-MM-DD HH:mm', tz);
        let end = moment.tz(`${date} ${endHm}`, 'YYYY-MM-DD HH:mm', tz);
        if (!end.isAfter(start)) end = end.add(1, 'day');
        setMatching(true);
        setMatchNotice(null);
        schedulingAvailabilityMatchService({
          scheduleId: id,
          starts_at: start.toISOString(),
          ends_at: end.toISOString(),
        })
          .then((r) => {
            const items = r.data?.items || [];
            setMatches(items);
            setMatchNotice(
              items.length
                ? null
                : {
                    type: 'info',
                    text:
                      'No one is marked available for this time. They may be off, outside this schedule’s member group, or have not saved availability.',
                  },
            );
          })
          .catch((err) => {
            setMatches([]);
            setMatchNotice({
              type: 'error',
              text:
                err?.response?.data?.message ||
                'Could not look up available people.',
            });
          })
          .finally(() => setMatching(false));
      })
      .catch(() => {
        setMatches([]);
        setMatchNotice({
          type: 'error',
          text: 'Select a date, start, and end before searching.',
        });
      });
  };

  const addMatchToAssignees = (person) => {
    const uid = userIdOf(person.user || person);
    if (!uid) return;
    const current = form.getFieldValue('assignees') || [];
    if (current.map(String).includes(uid)) {
      message.info(`${userName(person.user || person)} is already assigned`);
      return;
    }
    form.setFieldsValue({ assignees: [...current, uid] });
    message.success(`Added ${userName(person.user || person)}`);
  };

  const publishWeek = () => {
    if (!weekStart) return;
    setPublishing(true);
    schedulingSchedulePublishService(id, {
      from: weekStart.toISOString(),
      to: weekStart.clone().add(7, 'days').toISOString(),
      notify: publishNotify,
    })
      .then((r) => {
        handleApiSuccess(r);
        setPublishOpen(false);
        loadShifts();
      })
      .catch(handleApiError)
      .finally(() => setPublishing(false));
  };

  if (permissions == null || (loading && !schedule)) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!accessControls.can(permissions, 'Scheduling.read:any')) {
    return (
      <div style={{ padding: 24 }}>
        <Result
          status="403"
          title="Access denied"
          subTitle="You do not have permission to view Scheduling."
          extra={
            <Button type="primary" onClick={() => history.push('/')}>
              Go to Dashboard
            </Button>
          }
        />
      </div>
    );
  }

  if (!schedule) {
    return (
      <div style={{ padding: 24 }}>
        <Result
          status="404"
          title="Schedule not found"
          extra={
            <Button
              type="primary"
              onClick={() => history.push('/time-tracker/scheduling')}
            >
              Back to schedules
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="scheduling-board">
      <MyBreadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Time Tracker', path: '/time-tracker/tracker' },
          { label: 'Schedules', path: '/time-tracker/scheduling' },
          { label: schedule.name, path: '' },
        ]}
      />
      <PageHeader
        ghost={false}
        title={schedule.name}
        subTitle={
          heading ? `${heading.title} · ${heading.range}` : 'Weekly schedule'
        }
        onBack={() => history.push('/time-tracker/scheduling')}
        extra={
          <Space wrap>
            {canUpdate && (
              <Button
                icon={<SettingOutlined />}
                onClick={() =>
                  history.push('/time-tracker/scheduling/update/' + id)
                }
              >
                Settings
              </Button>
            )}
            {canUpdate && (
              <Button
                icon={<ScheduleOutlined />}
                type={draftCount ? 'primary' : 'default'}
                onClick={() => setPublishOpen(true)}
              >
                Publish week
                {draftCount ? ` (${draftCount} draft)` : ''}
              </Button>
            )}
            {canUpdate && (
              <Button icon={<ClearOutlined />} onClick={clearThisWeek}>
                Clear this week
              </Button>
            )}
            {canUpdate && (
              <Tooltip
                title={
                  undoStack.length
                    ? `Undo ${undoStack[undoStack.length - 1].label}`
                    : 'Nothing to undo'
                }
              >
                <Button
                  icon={<UndoOutlined />}
                  disabled={!undoStack.length}
                  loading={undoing}
                  onClick={undoLast}
                >
                  Undo
                </Button>
              </Tooltip>
            )}
            {canUpdate && (
              <Button
                type="primary"
                icon={<PlusCircleOutlined />}
                onClick={() => openShift(null, moment.tz(tz))}
                disabled={activities.length === 0}
              >
                Add shift
              </Button>
            )}
            <Button
              icon={<CalendarOutlined />}
              onClick={() => setAvailOpen(true)}
            >
              My availability
            </Button>
          </Space>
        }
      />

      {!canUpdate && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="View-only"
          description="You can view published shifts and claim open spots. Editing and publishing are limited to schedule managers."
        />
      )}

      {activities.length === 0 && canUpdate && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Create activities before adding shifts"
          description={
            <span>
              Activities label each shift.{' '}
              <Button
                type="link"
                size="small"
                style={{ padding: 0 }}
                onClick={() =>
                  history.push('/time-tracker/scheduling/activities')
                }
              >
                Manage activities
              </Button>
            </span>
          }
        />
      )}

      {clipboardShift && canUpdate && (
        <Alert
          type="success"
          showIcon
          closable
          onClose={() => setClipboardShift(null)}
          style={{ marginBottom: 16 }}
          message={`Copied: ${clipboardShift.activity?.name || 'Shift'}`}
          description="Click + on any day to paste this shift there (same times of day). Pasted shifts stay as drafts until you Publish."
        />
      )}

      {canUpdate && draftCount > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={`${draftCount} unpublished draft shift${
            draftCount === 1 ? '' : 's'
          } this week`}
          description="Staff only see published shifts for clock-in. Use Publish week when the roster is ready."
        />
      )}

      {hoursByUser.length > 0 && (
        <Card size="small" className="scheduling-hours-card" bordered={false}>
          <Text strong style={{ marginRight: 12 }}>
            Scheduled hours this week
          </Text>
          <Space wrap size={[8, 8]}>
            {hoursByUser.map((row) => (
              <Tooltip key={row.id} title={row.name}>
                <Tag>
                  {row.name}: {formatHours(row.seconds)}
                </Tag>
              </Tooltip>
            ))}
          </Space>
        </Card>
      )}

      <Card size="small" className="scheduling-toolbar-card" bordered={false}>
        <div className="scheduling-toolbar">
          <Space wrap>
            <Button
              icon={<LeftOutlined />}
              onClick={() =>
                setWeekStart((w) => w.clone().subtract(7, 'days'))
              }
            />
            <Button
              onClick={() => setWeekStart(getWeekStartMoment(moment(), tz))}
            >
              This week
            </Button>
            <Button
              icon={<RightOutlined />}
              onClick={() => setWeekStart((w) => w.clone().add(7, 'days'))}
            />
            {heading && (
              <Text strong style={{ marginLeft: 4 }}>
                {heading.range}
              </Text>
            )}
          </Space>
          <Space wrap>
            <GlobalOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
            <Text type="secondary">{getTrackerTimezoneSummary(tz)}</Text>
            <Select
              showSearch
              allowClear
              filterOption={false}
              optionFilterProp="label"
              value={storedTz || undefined}
              placeholder={schedule.timezone || 'Browser default'}
              style={{ width: 260 }}
              options={tzOptions}
              onSearch={setTzSearch}
              onDropdownVisibleChange={(open) => {
                if (!open) setTzSearch('');
              }}
              onChange={(v) => {
                const next = v || '';
                setStoredTz(next);
                saveStoredTimezone(next);
                setWeekStart(
                  getWeekStartMoment(
                    moment(),
                    getEffectiveTimezone(next || schedule.timezone || ''),
                  ),
                );
              }}
            />
          </Space>
        </div>
      </Card>

      <Spin spinning={loading}>
        <div className="scheduling-week">
          {days.map((day) => {
            if (!day) return null;
            const dayKey = day.format('YYYY-MM-DD');
            const isToday = dayKey === todayKey;
            const dayShifts = shifts.filter((s) => {
              const dragged =
                dragPreview && String(dragPreview.id) === String(s._id);
              const starts = dragged ? dragPreview.starts_at : s.starts_at;
              return moment.tz(starts, tz).format('YYYY-MM-DD') === dayKey;
            });
            return (
              <Card
                key={day.valueOf()}
                size="small"
                className={
                  'scheduling-day-card' +
                  (isToday ? ' scheduling-day-card--today' : '')
                }
                title={
                  <div className="scheduling-day-title">
                    <div>
                      <Text strong>{day.format('ddd')}</Text>
                      <br />
                      <Text type={isToday ? undefined : 'secondary'}>
                        {day.format('MMM D')}
                      </Text>
                      {isToday && (
                        <Tag color="blue" style={{ marginLeft: 6 }}>
                          Today
                        </Tag>
                      )}
                    </div>
                    {canUpdate && activities.length > 0 && (
                      <Space size={0}>
                        {clipboardShift && (
                          <Tooltip title="Paste copied shift">
                            <Button
                              type="link"
                              size="small"
                              icon={<SnippetsOutlined />}
                              onClick={() => pasteOntoDay(day)}
                            />
                          </Tooltip>
                        )}
                        <Tooltip title="Add shift">
                          <Button
                            type="link"
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={() => openShift(null, day)}
                          />
                        </Tooltip>
                      </Space>
                    )}
                  </div>
                }
              >
                <div
                  className="scheduling-day-drop"
                  data-day={dayKey}
                  style={{ height: grid.height }}
                >
                  <div
                    className="scheduling-day-grid"
                    style={{ height: grid.height }}
                  >
                    {Array.from(
                      { length: grid.end - grid.start + 1 },
                      (_, i) => (
                        <div
                          key={i}
                          className="scheduling-hour-line"
                          style={{ top: i * PX_PER_HOUR }}
                        >
                          {moment({ hour: grid.start + i }).format('h A')}
                        </div>
                      ),
                    )}
                    {(() => {
                      const { seats } = layoutDaySeats(dayShifts, dragPreview);
                      const dayLabel = day.format('ddd, MMM D');
                      return seats.map((seat) => {
                        const shift = seat.shift;
                        const layout = layoutForShift(
                          seat.starts,
                          seat.ends,
                          tz,
                          grid.start,
                        );
                        const open = !seat.assignee;
                        const color =
                          shift.color || shift.activity?.color || '#1890ff';
                        const isDraft = shift.published === false;
                        const person = open
                          ? 'Open'
                          : userName(seat.assignee.user || seat.assignee);
                        const activity = shift.activity?.name || 'Activity';
                        const raised =
                          raisedShiftId &&
                          String(raisedShiftId) === String(shift._id);
                        const laneCount = seat.laneCount || 1;
                        return (
                          <div
                            key={`${shift._id}-${seat.seatIndex}`}
                            className={
                              'scheduling-seat' +
                              (open ? ' scheduling-seat--open' : '') +
                              (isDraft ? ' scheduling-seat--draft' : '')
                            }
                            style={{
                              top: layout.top,
                              height: Math.min(
                                layout.height,
                                Math.max(28, grid.height - layout.top),
                              ),
                              left: `calc(${SEAT_GUTTER}px + ${seat.lane} * (100% - ${SEAT_GUTTER}px) / ${laneCount})`,
                              width: `calc((100% - ${SEAT_GUTTER}px) / ${laneCount} - 3px)`,
                              borderLeftColor: open ? '#ff4d4f' : color,
                              zIndex: raised ? 5 : 2,
                              cursor: canUpdate ? 'grab' : 'pointer',
                            }}
                            title={`${activity} · ${person}`}
                            role="button"
                            tabIndex={0}
                            onMouseDown={(ev) => {
                              if (!canUpdate) return;
                              setRaisedShiftId(String(shift._id));
                              startMoveShift(ev, shift);
                            }}
                            onClick={() => {
                              if (ignoreClickRef.current) {
                                ignoreClickRef.current = false;
                                return;
                              }
                              openSlotList(dayLabel, [{ shift }]);
                            }}
                          >
                            <span className="scheduling-seat-activity">
                              {activity}
                            </span>
                            <span className="scheduling-seat-person">
                              {person}
                            </span>
                            {canUpdate && (
                              <div
                                className="scheduling-shift-resize"
                                title="Drag to change length"
                                onMouseDown={(ev) => startResizeShift(ev, shift)}
                              />
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </Spin>

      <Modal
        title={slotList ? slotList.dayLabel : 'Shifts'}
        visible={!!slotList}
        open={!!slotList}
        onCancel={() => setSlotList(null)}
        footer={null}
        width={520}
        destroyOnClose
      >
        <div className="scheduling-slot-list">
          {(slotList?.ids || [])
            .map((sid) => shifts.find((shift) => String(shift._id) === sid))
            .filter(Boolean)
            .map((shift) => {
              const assigned = shift.assignees || [];
              const open = Math.max(
                0,
                Number(shift.capacity || 1) - assigned.length,
              );
              const mine = assigned.some(
                (a) => userIdOf(a.user) === currentUserId,
              );
              const color = shift.color || shift.activity?.color || '#1890ff';
              const isDraft = shift.published === false;
              const people = assigned.length
                ? assigned.map((a) => userName(a.user)).join(', ')
                : 'Unassigned';
              const openSwap = swaps.find(
                (s) => String(s.shift?._id || s.shift) === String(shift._id),
              );
              return (
                <div
                  key={shift._id}
                  className={
                    'scheduling-slot-row' +
                    (open > 0 ? ' scheduling-shift--open' : '') +
                    (isDraft ? ' scheduling-shift--draft' : '')
                  }
                  style={{ borderLeftColor: open > 0 ? '#ff4d4f' : color }}
                >
                  <div className="scheduling-slot-row-main">
                    <Text strong>{shift.activity?.name || 'Activity'}</Text>
                    <div className="scheduling-shift-time">
                      {moment.tz(shift.starts_at, tz).format('h:mm A')} –{' '}
                      {moment.tz(shift.ends_at, tz).format('h:mm A')}
                    </div>
                    <div className="scheduling-shift-users">{people}</div>
                    <Space size={4} wrap>
                      {isDraft && <Tag>Draft</Tag>}
                      {open > 0 ? (
                        <Tag color="error">{open} open</Tag>
                      ) : (
                        <Tag color="success">Filled</Tag>
                      )}
                    </Space>
                  </div>
                  <Space size={0} wrap className="scheduling-slot-row-actions">
                    {canUpdate && (
                      <Tooltip title="Edit">
                        <Button
                          type="text"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => {
                            setSlotList(null);
                            openShift(shift);
                          }}
                        />
                      </Tooltip>
                    )}
                    {canUpdate && (
                      <Tooltip title="Copy (paste onto another day)">
                        <Button
                          type="text"
                          size="small"
                          icon={<CopyOutlined />}
                          onClick={() => copyShift(shift)}
                        />
                      </Tooltip>
                    )}
                    {canUpdate && (
                      <Tooltip title="Duplicate to next week">
                        <Button
                          type="text"
                          size="small"
                          icon={<SnippetsOutlined />}
                          onClick={() => duplicateShift(shift._id)}
                        />
                      </Tooltip>
                    )}
                    {canUpdate && (
                      <Tooltip title="Delete">
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => deleteShift(shift)}
                        />
                      </Tooltip>
                    )}
                    {open > 0 && !mine && !isDraft && (
                      <Button
                        type="link"
                        size="small"
                        icon={<UserAddOutlined />}
                        onClick={() =>
                          runAction(schedulingShiftClaimService(shift._id))
                        }
                      >
                        Claim
                      </Button>
                    )}
                    {mine && !isDraft && !openSwap && (
                      <Button
                        type="link"
                        size="small"
                        icon={<SwapOutlined />}
                        onClick={() =>
                          runAction(schedulingShiftSwapRequestService(shift._id))
                        }
                      >
                        Request swap
                      </Button>
                    )}
                    {openSwap &&
                      userIdOf(openSwap.from_user) !== currentUserId &&
                      !mine && (
                        <Button
                          type="link"
                          size="small"
                          icon={<SwapOutlined />}
                          onClick={() =>
                            runAction(schedulingSwapClaimService(openSwap._id))
                          }
                        >
                          Take swap
                        </Button>
                      )}
                    {openSwap &&
                      userIdOf(openSwap.from_user) === currentUserId && (
                        <Button
                          type="link"
                          size="small"
                          onClick={() =>
                            runAction(schedulingSwapCancelService(openSwap._id))
                          }
                        >
                          Cancel swap
                        </Button>
                      )}
                  </Space>
                </div>
              );
            })}
        </div>
      </Modal>

      <Modal
        title={editing ? 'Edit shift' : 'Add shift'}
        visible={modalOpen}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={submitShift}
        confirmLoading={saving}
        width={640}
        destroyOnClose
        okText={editing ? 'Update' : 'Create draft'}
        okButtonProps={{ disabled: activities.length === 0 }}
      >
        {activities.length === 0 ? (
          <Alert
            type="warning"
            showIcon
            message="No activities"
            description="Create an activity first, then add shifts."
          />
        ) : (
          <Form
            key={modalKey}
            form={form}
            layout="vertical"
            className="scheduling-shift-form"
            preserve={false}
            initialValues={shiftDraft || undefined}
          >
            {!editing && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="New shifts are drafts until you Publish week"
              />
            )}
            <Form.Item
              name="activity"
              label="Activity"
              rules={[{ required: true, message: 'Select an activity' }]}
            >
              <Select
                showSearch
                optionFilterProp="children"
                placeholder="Select activity"
              >
                {activities.map((a) => (
                  <Option key={a._id} value={a._id}>
                    <Space>
                      <span
                        className="scheduling-color-swatch"
                        style={{ background: a.color || '#d9d9d9' }}
                      />
                      {a.name}
                    </Space>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Row gutter={16}>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="date"
                  label="Date"
                  rules={[{ required: true, message: 'Select a date' }]}
                >
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={12} sm={6} md={5}>
                <Form.Item
                  name="start_time"
                  label="Start"
                  rules={[{ required: true, message: 'Required' }]}
                  getValueProps={(value) => ({
                    value: value ? moment(value) : null,
                  })}
                >
                  <TimePicker
                    use12Hours
                    format="h:mm A"
                    minuteStep={5}
                    style={{ width: '100%' }}
                    inputReadOnly
                  />
                </Form.Item>
              </Col>
              <Col xs={12} sm={6} md={5}>
                <Form.Item
                  name="end_time"
                  label="End"
                  rules={[{ required: true, message: 'Required' }]}
                  getValueProps={(value) => ({
                    value: value ? moment(value) : null,
                  })}
                >
                  <TimePicker
                    use12Hours
                    format="h:mm A"
                    minuteStep={5}
                    style={{ width: '100%' }}
                    inputReadOnly
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item
                  name="capacity"
                  label="Capacity"
                  rules={[{ required: true, message: 'Required' }]}
                  extra="Open slots to claim"
                >
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="assignees"
              label="Assignees"
              extra={
                memberGroupsSet
                  ? 'Only people in this schedule’s member groups are listed. Leave empty for an open shift.'
                  : 'Leave empty for an open shift. Names show email so you pick the right account.'
              }
            >
              <Select
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="children"
                placeholder={
                  memberGroupsSet && pickerUsers.length === 0
                    ? 'No people in the member groups yet'
                    : 'Assign people or leave open'
                }
              >
                {[...assigneeChoices]
                  .sort((a, b) =>
                    userLabel(a).localeCompare(userLabel(b), undefined, {
                      sensitivity: 'base',
                    }),
                  )
                  .map((u) => (
                    <Option key={u._id} value={u._id}>
                      {userLabel(u)}
                    </Option>
                  ))}
              </Select>
            </Form.Item>

            {canUpdate && (
              <div style={{ marginBottom: 16 }}>
                <Button size="small" loading={matching} onClick={lookupMatches}>
                  Find available people
                </Button>
                {matchNotice && (
                  <Alert
                    style={{ marginTop: 8 }}
                    type={matchNotice.type}
                    showIcon
                    message={matchNotice.text}
                  />
                )}
                {matches.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary">
                      Available for this time — click a name to assign:{' '}
                    </Text>
                    {matches.map((m) => (
                      <Tag
                        key={userIdOf(m.user)}
                        color="blue"
                        style={{ cursor: 'pointer' }}
                        onClick={() => addMatchToAssignees(m)}
                      >
                        {userLabel(m.user)}
                      </Tag>
                    ))}
                  </div>
                )}
              </div>
            )}

            <Form.Item name="notes" label="Notes">
              <Input.TextArea rows={2} placeholder="Optional notes" />
            </Form.Item>

            <Form.Item name="use_color" valuePropName="checked">
              <Checkbox>Override activity color</Checkbox>
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(prev, cur) => prev.use_color !== cur.use_color}
            >
              {({ getFieldValue }) =>
                getFieldValue('use_color') ? (
                  <Form.Item name="color" label="Shift color">
                    <input
                      type="color"
                      aria-label="Shift color"
                      style={{
                        width: 56,
                        height: 36,
                        padding: 2,
                        border: '1px solid #d9d9d9',
                        borderRadius: 6,
                        cursor: 'pointer',
                        background: '#fff',
                      }}
                    />
                  </Form.Item>
                ) : null
              }
            </Form.Item>
          </Form>
        )}
      </Modal>

      <Modal
        title="Publish schedule"
        visible={publishOpen}
        open={publishOpen}
        onCancel={() => setPublishOpen(false)}
        onOk={publishWeek}
        confirmLoading={publishing}
        okText="Publish"
      >
        <p>
          Publish all shifts for{' '}
          <strong>{heading?.range || 'this week'}</strong> so staff can see them
          and clock in.
        </p>
        <Form layout="vertical">
          <Form.Item label="Notify">
            <Radio.Group
              value={publishNotify}
              onChange={(e) => setPublishNotify(e.target.value)}
            >
              <Space direction="vertical">
                <Radio value="everyone">
                  Email member and manager groups the full week. Each person’s
                  shifts are highlighted, and every shift shows who is assigned.
                </Radio>
                <Radio value="affected">
                  Email each assigned person only their own shifts, with a link
                  to the full schedule
                </Radio>
                <Radio value="none">Do not notify anyone</Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="My availability / time off"
        visible={availOpen}
        open={availOpen}
        onCancel={() => setAvailOpen(false)}
        footer={null}
        width={640}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Add blocks for this week. Available times help managers fill shifts. Time off hides you from matching."
        />
        <Form
          form={availForm}
          layout="vertical"
          initialValues={{ type: 'available' }}
        >
          <Form.Item name="type" label="Type" rules={[{ required: true }]}>
            <Radio.Group>
              <Radio value="available">Available</Radio>
              <Radio value="time_off">Time off</Radio>
            </Radio.Group>
          </Form.Item>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="date" label="Date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="start_time"
                label="Start"
                rules={[{ required: true }]}
              >
                <TimePicker
                  use12Hours
                  format="h:mm A"
                  minuteStep={15}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="end_time"
                label="End"
                rules={[{ required: true }]}
              >
                <TimePicker
                  use12Hours
                  format="h:mm A"
                  minuteStep={15}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="note" label="Note">
            <Input />
          </Form.Item>
          <Button type="primary" loading={availSaving} onClick={submitAvailability}>
            Save block
          </Button>
        </Form>
        <div style={{ marginTop: 16 }}>
          {(availability || [])
            .filter((b) => userIdOf(b.user) === currentUserId || canUpdate)
            .map((b) => (
              <div key={b._id} style={{ marginBottom: 8 }}>
                <Tag color={b.type === 'time_off' ? 'red' : 'green'}>
                  {b.type === 'time_off' ? 'Time off' : 'Available'}
                </Tag>
                {userName(b.user)} ·{' '}
                {moment.tz(b.starts_at, tz).format('ddd MMM D h:mm A')} –{' '}
                {moment.tz(b.ends_at, tz).format('h:mm A')}
                {(userIdOf(b.user) === currentUserId || canUpdate) && (
                  <Button
                    type="link"
                    size="small"
                    danger
                    onClick={() =>
                      schedulingAvailabilityDeleteService(b._id)
                        .then((r) => {
                          handleApiSuccess(r);
                          loadShifts();
                        })
                        .catch(handleApiError)
                    }
                  >
                    Remove
                  </Button>
                )}
              </div>
            ))}
        </div>
      </Modal>
    </div>
  );
}
